import { useUser } from "../../contexts/UserContext/hooks/useUser";
import Logo from "../../components/Logo";
import { PartialOrganization } from "../../contexts/UserContext/types";
import { useMutation } from "@apollo/client";
import { SwitchOrganizationDocument } from "../../__generated__/graphql";
import toast from "react-hot-toast";
import FullLoading from "../../components/FullLoading";
import { client } from "../../lib/apollo";
import { CheckIcon } from "@heroicons/react/24/outline";
import { cn } from "../../lib/utils";
import { motion } from "motion/react";

export default function SwitchOrganization() {
  const { organizations, setCurrentOrganization, currentOrganization } =
    useUser();

  const [switchOrganization, { loading: switchOrganizationLoading }] =
    useMutation(SwitchOrganizationDocument);

  const handleSwitchOrganization = async (organizationId: string) => {
    try {
      if (!organizations) {
        toast.error("Aucune organisation trouvée");
        return;
      }

      const { data } = await switchOrganization({
        variables: {
          organizationId,
        },
      });

      if (data?.switchOrganization?.token) {
        localStorage.setItem("token", data.switchOrganization.token);
      }

      const organization = organizations.find(
        (organization) => organization.id === organizationId
      );

      if (!organization) {
        toast.error("Organisation non trouvée");
        return;
      }

      setCurrentOrganization(organization);

      // Reset Apollo cache to clear data from previous organization
      await client.resetStore();

      toast.success("Organisation sélectionnée avec succès");
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la sélection de l'organisation"
      );
    }
  };

  const organizationButton = (organization: PartialOrganization) => {
    return (
      <div
        key={organization.id}
        className={cn(
          "flex flex-col gap-2 border border-border rounded-md p-4 w-full bg-dark cursor-pointer hover:bg-dark/80 transition-all duration-300 hover:border-accent",
          currentOrganization?.id === organization.id && "border-accent"
        )}
        onClick={() => handleSwitchOrganization(organization.id)}
      >
        <div className="flex items-center gap-2">
          {currentOrganization?.id === organization.id && (
            <div className=" bg-accent rounded-full p-1">
              <CheckIcon className="w-4 h-4" />
            </div>
          )}
          <div className="flex flex-col">
            <div className="text-primary text-xs">Nom de l'organisation</div>
            <div className="text-accent">{organization.name}</div>
          </div>
        </div>
      </div>
    );
  };

  if (switchOrganizationLoading) {
    return <FullLoading />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col items-center h-screen"
    >
      <div className="flex flex-col items-center justify-center h-[200px]">
        <Logo className="text-2xl" animated={false} />
      </div>
      <div className="flex flex-col border border-input rounded-md p-8 w-120 bg-dark">
        <h1 className="text-2xl font-bold">Sélectionnez l'organisation</h1>
        <div className="text-secondary text-sm">
          Vous faites partie de plusieurs organisations, veuillez en
          sélectionner une pour continuer.
        </div>

        <div className="flex flex-col gap-4 mt-4">
          {organizations?.map((organization) =>
            organizationButton(organization)
          )}
        </div>
      </div>
    </motion.div>
  );
}
